<template>
	<div class="tab-content-inner">
		<div class="filters-section">
			<div class="search-container">
				<label class="search-label">{{ $t("globals.search") }}</label>
				<b-form-input
					v-model="filter"
					class="search-input"
					:placeholder="$t('globals.type_to_search')"
				/>
			</div>

			<div class="action-buttons d-flex w-100 justify-content-end p-2">
				<button
					class="reload-btn"
					@click="reload"
					:disabled="loading"
					v-b-tooltip.hover
					:title="$t('globals.reload')"
				>
					<icon name="sync-alt" :spin="loading"></icon>
				</button>
			</div>
		</div>

		<div class="table-wrapper">
			<table class="modern-table">
				<thead>
					<tr>
						<th
							v-for="field in fields"
							:key="field.key"
							@click="field.sortable && handleSort(field.key)"
						>
							{{ field.label }}
							<span v-if="sortBy === field.key" class="sort-icon">
								{{ sortDesc ? '▼' : '▲' }}
							</span>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="item in paginatedItems" :key="item.id" class="table-row">
						<td>{{ item.id }}</td>
						<td>{{ item.full_name }}</td>
						<td>{{ item.email }}</td>
						<td>
							<span class="status-badge" :class="isDisplayed(item) ? 'status-completed' : 'status-cancelled'">
								{{ isDisplayed(item) ? $t("globals.displayed") : $t("globals.not_displayed") }}
							</span>
						</td>
						<td>
							<span class="status-badge" :class="isEmployed(item) ? 'status-completed' : 'status-cancelled'">
								{{ isEmployed(item) ? $t("globals.employed") : $t("globals.not_employed") }}
							</span>
						</td>
						<td >
							<button
								v-if="isEmployed(item)"
								:class="isDisplayed(item) ? 'action-btn-disable' : 'action-btn-enable'"
								:disabled="loading"
								@click="toggleDisplayed(item.id, !isDisplayed(item))"
							>
								{{ isDisplayed(item) ? $t("globals.disable") : $t("globals.enable") }}
							</button>
							
						</td>
					</tr>
				</tbody>
			</table>

			<div v-if="loading" class="loading-state">
				<div class="spinner"></div>
				<p>{{ $t("globals.loading") }}...</p>
			</div>
			<div v-else-if="filteredItems.length === 0" class="empty-state">
				<p>{{ $t("globals.no_results") }}</p>
			</div>
		</div>

		<div class="pagination-section">
			<div class="controls-left">
				<span class="summary-label">{{ $t("globals.per_page") }}</span>
				<b-form-select
					v-model="perPage"
					:options="perPageOptions"
					class="per-page-select"
				/>
				<span class="summary-label" style="margin-left: 1rem;">
					{{ $t("globals.showing") }}
					{{ ((currentPage - 1) * perPage) + 1 }}-{{ Math.min(currentPage * perPage, filteredItems.length) }}
					{{ $t("globals.of") }} {{ filteredItems.length }}
				</span>
			</div>
			<div class="pagination-controls">
				<button
					class="page-btn"
					:disabled="currentPage === 1"
					@click="currentPage = 1"
				>
					&lt;&lt;
				</button>
				<button
					class="page-btn"
					:disabled="currentPage === 1"
					@click="currentPage--"
				>
					&lt;
				</button>
				<span style="padding: 0 1rem; color: #6c757d; font-size: 0.875rem;">
					{{ $t("globals.page") }} {{ currentPage }} {{ $t("globals.of") }} {{ totalPages }}
				</span>
				<button
					class="page-btn"
					:disabled="currentPage >= totalPages"
					@click="currentPage++"
				>
					&gt;
				</button>
				<button
					class="page-btn"
					:disabled="currentPage >= totalPages"
					@click="currentPage = totalPages"
				>
					&gt;&gt;
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import { mapActions } from "vuex";
import "vue-awesome/icons/sync-alt";

export default {
	name: "AllAgents",
	components: {
		Icon: () => import("vue-awesome/components/Icon.vue"),
	},
	data() {
		return {
			items: [],
			filter: "",
			loading: false,
			sortBy: "id",
			sortDesc: false,
			currentPage: 1,
			perPage: 20,
			perPageOptions: [
				{ text: "20", value: 20 },
				{ text: "40", value: 40 },
				{ text: "60", value: 60 }
			],
			fields: [
				{
					key: "id",
					label: this.$t("globals.id"),
					sortable: true
				},
				{
					key: "full_name",
					label: this.$t("globals.name"),
					sortable: true
				},
                {
                    key: "email",
                    label: this.$t("globals.email"),
                    sortable: false
                },
                {
                    key: "displayed",
					label: this.$t("globals.displayed"),
                    sortable: false
				},
				{
					key: "is_employed",
					label: this.$t("globals.employed"),
					sortable: false
				},
				{
					key: "actions",
					label: this.$t("globals.actions"),
					sortable: false
                }
			]
		};
	},
	computed: {
		filteredItems() {
			if (!this.filter) {
				return this.sortedItems;
			}

			const searchTerm = this.filter.toLowerCase();
			return this.sortedItems.filter(item => {
				return (
					(item.id && item.id.toString().includes(searchTerm)) ||
					(item.full_name && item.full_name.toLowerCase().includes(searchTerm))
				);
			});
		},
		sortedItems() {
			const items = [...this.items];
			if (!this.sortBy) {
				return items;
			}

			return items.sort((a, b) => {
				const aVal = a[this.sortBy];
				const bVal = b[this.sortBy];

				if (aVal === bVal) return 0;

				let result = 0;
				if (aVal === null || aVal === undefined) {
					result = 1;
				} else if (bVal === null || bVal === undefined) {
					result = -1;
				} else {
					result = aVal < bVal ? -1 : 1;
				}

				return this.sortDesc ? -result : result;
			});
		},
		paginatedItems() {
			const start = (this.currentPage - 1) * this.perPage;
			const end = start + this.perPage;
			return this.filteredItems.slice(start, end);
		},
		totalPages() {
			return Math.ceil(this.filteredItems.length / this.perPage) || 1;
		}
	},
	methods: {
		...mapActions("axios_mod", ["simpleFetch", "simpleMutation", "clearAgentOptions"]),
		isDisplayed(item) {
			return item.is_visible === true || item.is_visible === "true" || item.is_visible === 1;
		},
		isEmployed(item) {
			return item.is_employed === true || item.is_employed === "true" || item.is_employed === 1;
		},
		toggleDisplayed(id, displayedSet) {
			const data = {
				query: `
					mutation {
						res: UpdateAgentDisplayed(id: ${parseInt(id)}, displayed: ${displayedSet}) {
							id
							displayed
						}
					}
				`
			};

			this.loading = true;
			this.simpleMutation({ data, this_ref: this })
				.then(() => {
					this.clearAgentOptions();
					this.fetchAgents();
				})
				.finally(() => {
					this.loading = false;
				});
		},
		fetchAgents() {
			const data = {

				query: `
					query {
						allAgents {
							id
							email
							firstname
							lastname
							full_name
							displayed
							is_employed
							is_visible
						}
					}
				`
			};

			this.loading = true;
			this.simpleFetch({ data, this_ref: this })
				.then(res => {
					if (res) {
						this.items = res.allAgents || [];
					}
				})
				.finally(() => {
					this.loading = false;
				});
		},
		handleSort(fieldKey) {
			if (this.sortBy === fieldKey) {
				this.sortDesc = !this.sortDesc;
			} else {
				this.sortBy = fieldKey;
				this.sortDesc = false;
			}
		},
		reload() {
			this.filter = "";
			this.currentPage = 1;
			this.sortBy = "id";
			this.sortDesc = false;
			this.fetchAgents();
		}
	},
	watch: {
		filter() {
			this.currentPage = 1;
		},
		perPage() {
			this.currentPage = 1;
		}
	},
	mounted() {
		this.fetchAgents();
	}
};
</script>

<style scoped>
.modern-table tbody tr,
.modern-table tbody tr:hover,
.modern-table tbody td {
	cursor: default;
}


</style>